import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import StatsSummaryCards from '../components/StatsSummaryCards';

interface SprintDataPoint {
  sprintId: string;
  sprintName: string;
  piName: string;
  costs: number;
  revenue: number;
  costOverRevenue: number;
}

interface TeamSummary {
  teamId: string;
  teamName: string;
  projectId: string;
  projectName: string;
  totalCosts: number;
  totalRevenue: number;
  costOverRevenue: number;
  sprintData: SprintDataPoint[];
}

interface DashboardSummary {
  year: number;
  teams: TeamSummary[];
}

interface ProjectSummary {
  projectId: string;
  projectName: string;
  teams: TeamSummary[];
  totalCosts: number;
  totalRevenue: number;
  costOverRevenue: number;
  sprintData: SprintDataPoint[];
}

const currentYear = new Date().getFullYear();


// Aggregate teams into project summaries
function aggregateByProject(teams: TeamSummary[]): ProjectSummary[] {
  const projectMap = new Map<string, ProjectSummary>();

  for (const team of teams) {
    if (!projectMap.has(team.projectId)) {
      projectMap.set(team.projectId, {
        projectId: team.projectId,
        projectName: team.projectName,
        teams: [],
        totalCosts: 0,
        totalRevenue: 0,
        costOverRevenue: 0,
        sprintData: [],
      });
    }

    const project = projectMap.get(team.projectId)!;
    project.teams.push(team);
    project.totalCosts += team.totalCosts;
    project.totalRevenue += team.totalRevenue;
  }

  // Aggregate sprint data by sprint name for each project
  for (const project of projectMap.values()) {
    const sprintMap = new Map<string, SprintDataPoint>();

    for (const team of project.teams) {
      for (const sprint of team.sprintData) {
        if (!sprintMap.has(sprint.sprintId)) {
          sprintMap.set(sprint.sprintId, {
            sprintId: sprint.sprintId,
            sprintName: sprint.sprintName,
            piName: sprint.piName,
            costs: 0,
            revenue: 0,
            costOverRevenue: 0,
          });
        }
        const aggregated = sprintMap.get(sprint.sprintId)!;
        aggregated.costs += sprint.costs;
        aggregated.revenue += sprint.revenue;
      }
    }

    // Calculate C/R for each aggregated sprint
    for (const sprint of sprintMap.values()) {
      sprint.costOverRevenue = sprint.revenue > 0 ? sprint.costs / sprint.revenue : 0;
    }

    project.sprintData = Array.from(sprintMap.values()).sort((a, b) =>
      a.sprintName.localeCompare(b.sprintName)
    );

    // Calculate project C/R
    project.costOverRevenue = project.totalRevenue > 0 ? project.totalCosts / project.totalRevenue : 0;
  }

  return Array.from(projectMap.values()).sort((a, b) => a.projectName.localeCompare(b.projectName));
}

export default function Dashboard() {
  const api = useApi();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', selectedYear],
    queryFn: () => api.get<DashboardSummary>(`/api/dashboard/summary?year=${selectedYear}`),
  });

  // Year options
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <p className="text-primary-400">Failed to load dashboard data</p>
      </div>
    );
  }

  const teams = data?.teams || [];
  const projects = aggregateByProject(teams);

  return (
    <div className="space-y-8">
      {/* Header with year selector */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Overview of project and team performance</p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="year-select" className="text-sm text-gray-600 dark:text-gray-400">
            Year:
          </label>
          <select
            id="year-select"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="input w-24"
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Projects with team summaries */}
      {projects.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">No teams with completed sprints for {selectedYear}</p>
          <Link to="/projects" className="btn-primary mt-4 inline-block">
            View Projects
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {projects.map((project) => (
            <ProjectSection key={project.projectId} project={project} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectSection({ project }: { project: ProjectSummary }) {
  return (
    <div className="card border-2 border-primary-100 dark:border-primary-200 shadow-lg">
      {/* Project Header */}
      <div className="flex items-start gap-3 mb-5">
        <div className="w-2 h-12 bg-primary-500 rounded-full flex-shrink-0"></div>
        <div className="flex-1">
          <Link
            to={`/projects/${project.projectId}`}
            className="text-xl font-bold text-gray-900 dark:text-white hover:text-primary-500"
          >
            {project.projectName}
          </Link>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {project.teams.length} team{project.teams.length !== 1 ? 's' : ''} &middot; {project.sprintData.length} sprints
          </p>
        </div>
      </div>

      {/* Project Totals - Highlighted Section */}
      <div className="mb-6">
        <StatsSummaryCards totalCosts={project.totalCosts} totalRevenue={project.totalRevenue} costOverRevenue={project.costOverRevenue} />
      </div>

      {/* Team Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {project.teams.map((team) => (
          <TeamSummaryCard key={team.teamId} team={team} />
        ))}
      </div>
    </div>
  );
}

function TeamSummaryCard({ team }: { team: TeamSummary }) {
  return (
    <div className="bg-gray-50 dark:bg-dark-700 rounded-lg border border-gray-200 dark:border-dark-500 p-4 hover:border-primary-300 hover:shadow-md transition-all">
      {/* Team name */}
      <Link
        to={`/teams/${team.teamId}`}
        className="font-semibold text-gray-900 dark:text-white hover:text-primary-500 flex items-center gap-2 mb-3"
      >
        <span className="w-2 h-2 rounded-full bg-primary-400"></span>
        {team.teamName}
      </Link>

      {/* Metrics grid */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-gray-100 dark:bg-dark-600 rounded p-2">
          <p className="text-xs text-gray-600 dark:text-gray-400">Costs</p>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            {team.totalCosts.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
          </p>
        </div>
        <div className="bg-gray-100 dark:bg-dark-600 rounded p-2">
          <p className="text-xs text-gray-600 dark:text-gray-400">Revenue</p>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            {team.totalRevenue.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
          </p>
        </div>
        <div className="bg-gray-100 dark:bg-dark-600 rounded p-2">
          <p className="text-xs text-gray-600 dark:text-gray-400">C/R</p>
          <p className={`text-sm font-semibold ${team.costOverRevenue > 1 ? 'text-primary-400' : 'text-accent-aquamarine'}`}>
            {team.costOverRevenue.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Sprint count */}
      <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
        {team.sprintData.length} sprint{team.sprintData.length !== 1 ? 's' : ''} completed
      </p>
    </div>
  );
}
